import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as XLSX from 'xlsx';
import { UserService } from '../services/user/user.service';
import { lastValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import moment from 'moment';
import {
  DynamicListingComponent,
  FilterConfig,
  ColumnConfig,
  ActionConfig,
  ActionEvent,
  FilterEvent,
} from '../dynamic-listing/dynamic-listing';
import { SVG_ICONS } from '../utils/svg-icons.utils';
import { ToasterService } from '@ui-nuvertos/auth';

@Component({
  selector: 'lib-user-list',
  standalone: true,
  imports: [CommonModule, DynamicListingComponent],
  templateUrl: './user-list.html',
  styleUrl: './user-list.css',
})
export class UserList implements OnInit {
  exportFilename = `users_${new Date().toISOString().slice(0, 10)}`;

  tableData: any[] = [];
  totalCount: number = 0;
  totalPages: number = 0;
  isLoading: boolean = false;
  pageIndex: number = 1;
  rowsPerPage: number = 10;

  // Filter values
  name: string = '';
  email: string = '';
  phone: string = '';
  showInactives = false;
  role: string = '';

  roleOptions = [
    { label: 'All', value: '' },
    { label: 'Admin', value: 'admin' },
    { label: 'User', value: 'user' },
    { label: 'Manager', value: 'manager' },
  ];

  filterConfigs: FilterConfig[] = [];

  columnConfigs: ColumnConfig[] = [
    { key: 'name', header: 'Name' },
    { key: 'email', header: 'Email' },
    { key: 'phone', header: 'Phone' },
    { key: 'role', header: 'Role' },
    { key: 'created_at', header: 'Created Date' },
  ];

  actionConfigs: ActionConfig[] = [
    {
      key: 'toggle',
      label: 'Status',
      tooltip: 'Toggle Active/Inactive',
      type: 'toggle'
    },
    {
      key: 'view',
      label: 'View',
      tooltip: 'View',
      severity: 'secondary',
      svgIcon: SVG_ICONS.VIEW
    },
    {
      key: 'edit',
      label: 'Edit',
      tooltip: 'Edit',
      severity: 'secondary',
      svgIcon: SVG_ICONS.EDIT
    }
  ];

  constructor(
    private router: Router,
    private userService: UserService,
    private toasterService: ToasterService
  ) {}

  async ngOnInit() {
    this.initializeFilterConfigs();
    await this.loadUsers();
  }

  initializeFilterConfigs() {
    this.filterConfigs = [
      {
        key: 'name',
        type: 'text',
        placeholder: 'Name',
        value: this.name,
        showInAccordion: true,
        showInDialog: false
      },
      {
        key: 'email',
        type: 'text',
        placeholder: 'Email',
        value: this.email,
        showInAccordion: true,
        showInDialog: false
      },
      {
        key: 'phone',
        type: 'text',
        placeholder: 'Phone',
        value: this.phone,
        showInAccordion: true,
        showInDialog: false
      }
      // {
      //   key: 'role',
      //   type: 'select',
      //   placeholder: 'Role',
      //   options: this.roleOptions,
      //   optionLabel: 'label',
      //   optionValue: 'value',
      //   value: this.role,
      //   showInAccordion: true,
      //   showInDialog: false
      // }
    ];
  }

  async loadUsers(page: number = 1) {
    try {
      this.isLoading = true;
      this.pageIndex = page;

      const queryParams: any = {
        offset: (this.pageIndex - 1) * this.rowsPerPage,
        limit: this.rowsPerPage,
      };
      
      if (this.showInactives) {
        queryParams.include_inactive = 'true';
      }

      // Attach filters
      if (this.name && this.name.trim()) {
        queryParams.name = this.name.trim();
      }
      if (this.email && this.email.trim()) {
        queryParams.email = this.email.trim();
      }
      if (this.phone && this.phone.trim()) {
        queryParams.phone = this.phone.trim();
      }
      // if (this.role && this.role.trim()) {
      //   queryParams.role = this.role.trim();
      // }

      const userResponse = await lastValueFrom(this.userService.getAllUsers(queryParams));
      
      if (userResponse?.users && Array.isArray(userResponse.users)) {
        const rows = userResponse.users || [];
        this.tableData = rows.map((row: any) => {
          // Extract role from centers array (first center's role)
          const userRole = row.centers && row.centers.length > 0 
            ? row.centers[0].role 
            : '-';
          
          return {
            id: row.id,
            name: row.username || '-',
            email: row.email || '-',
            phone: row.phone_number || '-',
            role: userRole,
            is_active: row.is_active,
            created_at: moment(row.created_at).format('DD/MM/YYYY') || '-',
            raw: row
          };
        });
        this.totalCount = userResponse.total ?? 0;
        const totalPagesFromResponse = userResponse.total_pages ?? Math.ceil((this.totalCount || 0) / this.rowsPerPage);
        this.totalPages = totalPagesFromResponse > 0 ? totalPagesFromResponse : (this.tableData.length > 0 ? 1 : 0);
      } else {
        this.tableData = [];
        this.totalCount = 0;
        this.totalPages = 0;
      }

    } catch (error) {
      console.log('Error loading users:', error);
      this.tableData = [];
      this.totalCount = 0;
      this.totalPages = 0;
      this.toasterService.error('Error', 'Failed to load users');
    } finally {
      this.isLoading = false;
    }
  }

  onFilterEvent(event: FilterEvent) {
    // Update local filter values from event.data
    this.name = event.data?.name || '';
    this.email = event.data?.email || '';
    this.phone = event.data?.phone || '';
    // this.role = event.data?.role || '';
    
    // Update filter configs
    this.initializeFilterConfigs();
    
    this.pageIndex = 1;
    this.loadUsers(1);
  }

  onActionClicked(event: ActionEvent) {
    const row = event.row;
    if (!row) return;

    switch (event.action) {
      case 'toggle':
        this.toggleStatus(row);
        break;
      case 'view':
        this.router.navigate(['/settings/account_settings/users/view', row.id]);
        break;
      case 'edit':
        this.router.navigate(['/settings/account_settings/users/edit', row.id]);
        break;
    }
  }

  async toggleStatus(row: any) {
    // The toggle has already updated row.is_active via two-way binding
    // So we use the current value directly, ensuring it's a boolean
    const newStatus = Boolean(row.is_active);
    const statusText = newStatus ? 'activate' : 'deactivate';
    
    if (confirm(`Are you sure you want to ${statusText} "${row.name || row.id}"?`)) {
      try {
        this.isLoading = true;
        
        // Send only id and is_active (convert id to number)
        const updateData = {
          id: Number(row.id),
          is_active: newStatus
        };
        
        await lastValueFrom(
          this.userService.updateUser(updateData)
        );
        
        this.toasterService.success('Success', `User ${statusText}d successfully`);
        // Reload the list
        await this.loadUsers(this.pageIndex);
      } catch (error) {
        console.error('Error toggling status:', error);
        this.toasterService.error('Error', 'Failed to toggle user status');
        // Revert the toggle on error
        row.is_active = !newStatus;
      } finally {
        this.isLoading = false;
      }
    } else {
      // Revert the toggle if user cancels
      row.is_active = !newStatus;
    }
  }

  onPageChanged(page: number) {
    this.loadUsers(page);
  }

  onShowInactivesChange() {
    this.pageIndex = 1;
    this.loadUsers(this.pageIndex);
  }

  onColumnsSelected(selectedColumns: string[]) {
    // TODO: Integrate with backend column preferences
    // For now, simply reload data to reflect any necessary transformations
    this.loadUsers(this.pageIndex);
  }

  exportCsv() {
    const data = this.tableData.map(row => ({
      Name: row.name,
      Email: row.email,
      Phone: row.phone,
      Role: row.role,
      Status: row.is_active ? 'Active' : 'Inactive',
      'Created Date': row.created_at
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, `${this.exportFilename}.csv`);
  }

  exportXlsx() {
    const data = this.tableData.map(row => ({
      Name: row.name,
      Email: row.email,
      Phone: row.phone,
      Role: row.role,
      Status: row.is_active ? 'Active' : 'Inactive',
      'Created Date': row.created_at
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, `${this.exportFilename}.xlsx`);
  }

  exportPdf() {
    // PDF export implementation can be added here
    this.toasterService.info('Info', 'PDF export coming soon');
  }

  toTitleCase(str: string): string {
    return str.replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }
}

